import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import QRCode from 'qrcode';
import {
  buildVerificationLink,
  makeCertId,
} from '../utils/certificateUtils';
import SEOMeta from '../components/SEOMeta/SEOMeta';
import './certificate.css';

const defaultControls = {
  nameX: 790,
  nameY: 450,
  idX: 250,
  idY: 980,
  qrX: 1280,
  qrY: 710,
  qrSize: 170,
};

const hardcodedPlacement = {
  nameX: 790,
  nameY: 450,
  idX: 250,
  idY: 980,
  qrX: 1280,
  qrY: 710,
};

const extractStudentNames = (rows) => {
  if (!rows.length) return [];

  const firstRow = rows[0].map((cell) => String(cell).trim().toLowerCase());
  const nameColIndex = firstRow.findIndex((value) => value === 'full name' || value === 'name' || value === 'student name');

  if (nameColIndex >= 0) {
    return rows
      .slice(1)
      .map((row) => String(row[nameColIndex] || '').trim())
      .filter(Boolean);
  }

  return rows
    .map((row) => String(row[0] || '').trim())
    .filter(Boolean);
};

const loadImageFromUrl = (url) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('Failed to load certificate template image.'));
  image.src = url;
});

const canvasToBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (!blob) {
      reject(new Error('Unable to generate certificate image blob.'));
      return;
    }
    resolve(blob);
  }, 'image/png');
});

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

const CertificatesPage = () => {
  const [templateUrl, setTemplateUrl] = useState('');
  const [templateImage, setTemplateImage] = useState(null);
  const [previewImage, setPreviewImage] = useState('');
  const [students, setStudents] = useState([]);
  const [controls, setControls] = useState(defaultControls);
  const [baseVerificationUrl, setBaseVerificationUrl] = useState('https://www.algodoor.com');
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [uploadedCertificates, setUploadedCertificates] = useState([]);

  const hasReadyInputs = templateImage && students.length > 0;

  const samplePayload = useMemo(() => {
    const certId = makeCertId(0);
    return { certId };
  }, []);

  const previewLink = useMemo(
    () => buildVerificationLink(baseVerificationUrl, samplePayload.certId),
    [baseVerificationUrl, samplePayload],
  );

  const handleTemplateUpload = async (event) => {
    setError('');
    const file = event.target.files?.[0];

    if (!file) return;

    const fileUrl = URL.createObjectURL(file);
    setTemplateUrl(fileUrl);

    try {
      const image = await loadImageFromUrl(fileUrl);
      setTemplateImage(image);
    } catch (uploadError) {
      setTemplateImage(null);
      setError(uploadError.message);
    }
  };

  const handleExcelUpload = async (event) => {
    setError('');
    const file = event.target.files?.[0];

    if (!file) return;

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, {
        header: 1,
        blankrows: false,
        defval: '',
      });
      const names = extractStudentNames(rows);

      if (!names.length) {
        setError('No student names found in the file. Use first column or a Full Name header.');
        setStudents([]);
        return;
      }

      setStudents(names);
      setProgress(`${names.length} students loaded from Excel.`);
    } catch (uploadError) {
      setStudents([]);
      setError('Could not read Excel file. Upload a valid .xlsx or .xls file.');
    }
  };

  const handleControlChange = (field) => (event) => {
    const value = Number(event.target.value);
    setControls((prev) => ({ ...prev, [field]: Number.isNaN(value) ? 0 : value }));
  };

  const drawCertificate = async ({ name, certId }) => {
    const verificationLink = buildVerificationLink(baseVerificationUrl, certId);
    const qrDataUrl = await QRCode.toDataURL(verificationLink, {
      margin: 1,
      width: controls.qrSize,
    });

    const qrImage = await loadImageFromUrl(qrDataUrl);

    const canvas = document.createElement('canvas');
    canvas.width = templateImage.width;
    canvas.height = templateImage.height;
    const context = canvas.getContext('2d');

    context.drawImage(templateImage, 0, 0);

    context.fillStyle = '#111827';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.font = '700 56px Georgia, serif';
    context.fillText(name, hardcodedPlacement.nameX, hardcodedPlacement.nameY);

    context.textAlign = 'left';
    context.font = '600 18px Arial, sans-serif';
    context.fillText(`${certId}`, hardcodedPlacement.idX, hardcodedPlacement.idY);

    context.drawImage(qrImage, hardcodedPlacement.qrX, hardcodedPlacement.qrY, controls.qrSize, controls.qrSize);

    const blob = await canvasToBlob(canvas);

    return {
      canvas,
      blob,
      certId,
      name,
      verificationLink,
    };
  };

  const generateCertificates = async () => {
    if (!hasReadyInputs) return;

    setIsGenerating(true);
    setError('');
    setUploadedCertificates([]);

    try {
      const zip = new JSZip();
      const records = [];

      for (let index = 0; index < students.length; index += 1) {
        const name = students[index];
        const certId = makeCertId(index);

        setProgress(`Generating ${index + 1} of ${students.length}...`);
        // Sequential generation keeps memory usage stable for large batches.
        // eslint-disable-next-line no-await-in-loop
        const result = await drawCertificate({ name, certId });

        const fileName = `${certId}.png`;
        zip.file(fileName, result.blob);

        const certVerificationLink = buildVerificationLink(baseVerificationUrl, certId);
        records.push(`${certId},"${name.replace(/"/g, '""')}",${certVerificationLink}`);
        setUploadedCertificates((prev) => [
          ...prev,
          {
            certId,
            name,
            verifyUrl: certVerificationLink,
          },
        ]);
      }

      zip.file(
        'certificate-records.csv',
        ['certificate_id,full_name,verification_url', ...records].join('\n'),
      );

      const finalZip = await zip.generateAsync({ type: 'blob' });
      downloadBlob(finalZip, `algodoor-certificates-${Date.now()}.zip`);
      setProgress(`Done. ${students.length} certificates generated. Files are named by unique certificate ID.`);
    } catch (generationError) {
      setError(generationError.message || 'Certificate generation failed.');
    } finally {
      setIsGenerating(false);
    }
  };

  const renderPreview = async () => {
    if (!templateImage) return;

    try {
      const result = await drawCertificate({
        name: students[0] || 'Student Name',
        certId: makeCertId(0),
      });
      setPreviewImage(result.canvas.toDataURL('image/png'));
    } catch (previewError) {
      setError(previewError.message || 'Preview generation failed.');
    }
  };

  return (
    <div className="cert-page">
      <SEOMeta
        title="Generate Student Certificates | Algodoor Training Institute"
        description="Bulk certificate generation tool with custom text placement, QR code verification, and automated email distribution."
        keywords="certificate generator, student certificates, verification"
        url="/certificates"
        type="WebPage"
        noindex
      />
      <div className="cert-shell">
        <header className="cert-header">
          <div>
            <h1>Student Certificate Studio</h1>
            <p className="cert-sub">Upload one certificate template and one Excel file, then download all certificates in a ZIP.</p>
          </div>
          <a className="cert-link" href="/">Back to Home</a>
        </header>

        <div className="cert-grid">
          <section className="cert-card">
            <h2>Inputs</h2>
            <div className="cert-field">
              <label htmlFor="templateFile">
                Blank certificate image (PNG/JPG)
                <input id="templateFile" type="file" accept="image/png,image/jpeg,image/webp" onChange={handleTemplateUpload} />
              </label>
            </div>
            <div className="cert-field">
              <label htmlFor="studentExcel">
                Excel with names (.xlsx/.xls)
                <input id="studentExcel" type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} />
              </label>
            </div>
            <div className="cert-field">
              <label htmlFor="verificationBase">
                Public site URL for QR redirects
                <input
                  id="verificationBase"
                  type="text"
                  value={baseVerificationUrl}
                  onChange={(event) => setBaseVerificationUrl(event.target.value.trim() || 'https://www.algodoor.com')}
                />
              </label>
              <p className="cert-help">Preview verification link: {previewLink}</p>
            </div>

            <h2>Placement Controls</h2>
            <div className="cert-inline">
              <div className="cert-field">
                <label htmlFor="nameX">
                  Name X
                  <input id="nameX" type="number" value={hardcodedPlacement.nameX} readOnly />
                </label>
              </div>
              <div className="cert-field">
                <label htmlFor="nameY">
                  Name Y
                  <input id="nameY" type="number" value={hardcodedPlacement.nameY} readOnly />
                </label>
              </div>
            </div>
            <div className="cert-inline">
              <div className="cert-field">
                <label htmlFor="idX">
                  ID X
                  <input id="idX" type="number" value={hardcodedPlacement.idX} readOnly />
                </label>
              </div>
              <div className="cert-field">
                <label htmlFor="idY">
                  ID Y
                  <input id="idY" type="number" value={hardcodedPlacement.idY} readOnly />
                </label>
              </div>
            </div>
            <div className="cert-inline">
              <div className="cert-field">
                <label htmlFor="qrX">
                  QR X
                  <input id="qrX" type="number" value={hardcodedPlacement.qrX} readOnly />
                </label>
              </div>
              <div className="cert-field">
                <label htmlFor="qrY">
                  QR Y
                  <input id="qrY" type="number" value={hardcodedPlacement.qrY} readOnly />
                </label>
              </div>
            </div>
            <div className="cert-inline">
              <div className="cert-field">
                <label htmlFor="qrSize">
                  QR Size
                  <input id="qrSize" type="number" value={controls.qrSize} onChange={handleControlChange('qrSize')} />
                </label>
              </div>
              <div className="cert-field">
                <label htmlFor="fixedFontInfo">
                  Font Sizes
                  <input id="fixedFontInfo" type="text" value="Name 56px, ID 18px" readOnly />
                </label>
              </div>
            </div>

            <div className="cert-actions">
              <button type="button" className="cert-btn alt" onClick={renderPreview} disabled={!templateImage}>
                Render Preview
              </button>
              <button type="button" className="cert-btn" onClick={generateCertificates} disabled={!hasReadyInputs || isGenerating}>
                {isGenerating ? 'Generating...' : 'Generate & Download ZIP'}
              </button>
            </div>
            {uploadedCertificates.length > 0 && (
              <div className="cert-upload-table-wrap">
                <h3>Generated Certificates</h3>
                <table className="cert-upload-table">
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>Certificate ID</th>
                      <th>Verification Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadedCertificates.map((item) => (
                      <tr key={item.certId}>
                        <td>{item.name}</td>
                        <td>{item.certId}</td>
                        <td>
                          <a href={item.verifyUrl} target="_blank" rel="noreferrer" className="cert-link">
                            Open Verification
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {progress && <p className="cert-progress">{progress}</p>}
            {error && <p className="cert-error">{error}</p>}
          </section>

          <section className="cert-card">
            <h2>Preview</h2>
            <div className="cert-preview">
              {!templateImage && <p className="cert-help">Upload a template and click Render Preview.</p>}
              {templateImage && !previewImage && (
                <img src={templateUrl} alt="Certificate template preview" />
              )}
              {previewImage && <img src={previewImage} alt="Certificate generated preview" />}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CertificatesPage;
